import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
  FaWhatsapp,
  FaPhoneAlt,
  FaQuoteLeft,
  FaStar,
  FaRegStar,
  FaPassport,
  FaBriefcase,
  FaGraduationCap,
  FaPlaneDeparture,
  FaBuilding,
  FaGlobe,
  FaSpinner,
} from "react-icons/fa";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import CountUp from "react-countup";

import "swiper/css";
import "swiper/css/pagination";

const BACKEND_URL = process.env.REACT_APP_API_URL || "http://localhost:5001";

const CATEGORIES = ["All", "Work Visa", "Student Visa", "PR Visa", "Tourist Visa", "Business Visa"];

const CATEGORY_ICONS = {
  "Work Visa": FaBriefcase,
  "Student Visa": FaGraduationCap,
  "PR Visa": FaPassport,
  "Tourist Visa": FaPlaneDeparture,
  "Business Visa": FaBuilding,
  All: FaGlobe,
};

function StarRating({ rating }) {
  return (
    <div className="flex gap-1 text-yellow-400 text-lg justify-center">
      {[1, 2, 3, 4, 5].map((i) =>
        i <= rating ? <FaStar key={i} /> : <FaRegStar key={i} />
      )}
    </div>
  );
}

function Testimonials() {
  const [activeCategory, setActiveCategory] = useState("All");
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/testimonials`)
      .then((r) => {
        if (!r.ok) throw new Error("Failed to load testimonials");
        return r.json();
      })
      .then((json) => {
        setTestimonials(json.data || []);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  const filtered =
    activeCategory === "All"
      ? testimonials
      : testimonials.filter((t) => t.category === activeCategory);

  const featured = testimonials.filter((t) => t.rating === 5).slice(0, 4);

  const avatarSrc = (t) =>
    t.imageUrl
      ? `https://randomuser.me/api/portraits/${t.imageUrl}.jpg`
      : `https://ui-avatars.com/api/?name=${encodeURIComponent(t.name)}&background=1d4ed8&color=fff`;

  const flagSrc = (t) =>
    t.country
      ? `https://flagcdn.com/w40/${t.country}.png`
      : null;

  return (
    <div>
      <Helmet>
        <title>Client Testimonials | Lumis Documents Clearing Dubai</title>
        <meta name="description" content="Read real success stories from hundreds of happy clients who successfully secured their visas through Lumis Documents Clearing." />
      </Helmet>
      {/* HERO */}
      <section
        className="h-[420px] flex items-center justify-center text-white bg-cover bg-center relative"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1521737604893-d14cc237f11d?q=80&w=2068&auto=format&fit=crop')",
        }}
      >
        <div className="absolute inset-0 bg-black/60" />

        <div className="relative text-center px-6">
          <p className="tracking-widest text-blue-400 text-sm mb-3 uppercase">
            Client Success Stories
          </p>

          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            What Our Clients Say
          </h1>

          <p className="text-gray-200 max-w-xl mx-auto mb-5">
            Hundreds of satisfied clients have trusted Lumis Documents Clearing
            to navigate their immigration journey. Here are their stories.
          </p>

          <div className="text-gray-300 text-sm">
            <Link to="/" className="hover:text-white transition">
              Home
            </Link>
            <span className="mx-2">/</span>
            <span className="text-white font-medium">Testimonials</span>
          </div>
        </div>
      </section>

      {/* STATS BAR */}
      <section className="bg-blue-600 text-white py-10">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { end: 800, suffix: "+", label: "Happy Clients" },
            { end: 98, suffix: "%", label: "Success Rate" },
            { end: 1200, suffix: "+", label: "Visas Processed" },
            { end: 5, suffix: "★", label: "Average Rating" },
          ].map(({ end, suffix, label }) => (
            <div key={label}>
              <h3 className="text-3xl font-bold">
                <CountUp
                  end={end}
                  duration={2.5}
                  enableScrollSpy
                  scrollSpyDelay={200}
                />
                {suffix}
              </h3>
              <p className="text-blue-100 text-sm mt-1">{label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED TESTIMONIALS SLIDER */}
      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-3">
            Featured Stories
          </h2>
          <p className="text-center text-gray-500 mb-14">
            Real experiences from our most successful immigration cases
          </p>

          {loading ? (
            <div className="flex justify-center items-center h-40">
              <FaSpinner className="animate-spin text-blue-600 text-3xl" />
            </div>
          ) : error ? (
            <p className="text-center text-red-500">{error}</p>
          ) : (
            <Swiper
              modules={[Autoplay, Pagination]}
              slidesPerView={1}
              spaceBetween={30}
              loop={featured.length > 1}
              speed={800}
              autoplay={{ delay: 4000, disableOnInteraction: false, pauseOnMouseEnter: true }}
              pagination={{ clickable: true }}
              breakpoints={{
                768: { slidesPerView: 2 },
                1024: { slidesPerView: 2 },
              }}
              className="pb-14"
            >
              {featured.map((t) => (
                <SwiperSlide key={t._id}>
                  <div className="bg-gradient-to-br from-blue-600 to-blue-800 text-white rounded-2xl p-8 shadow-xl relative overflow-hidden h-full">
                    <FaQuoteLeft className="text-white/10 text-7xl absolute top-4 right-4" />

                    <div className="flex items-center gap-4 mb-5">
                      <img
                        src={avatarSrc(t)}
                        alt={t.name}
                        className="w-16 h-16 rounded-full border-2 border-white/40 object-cover"
                        onError={(e) => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(t.name)}&background=1d4ed8&color=fff`; }}
                      />
                      <div>
                        <h4 className="font-bold text-lg">{t.name}</h4>
                        <p className="text-blue-200 text-sm">{t.visaType}</p>
                        <div className="flex gap-1 text-yellow-300 mt-1">
                          {[...Array(t.rating)].map((_, i) => (
                            <FaStar key={i} className="text-sm" />
                          ))}
                        </div>
                      </div>
                    </div>

                    <p className="text-blue-100 text-sm leading-relaxed italic">
                      "{t.text}"
                    </p>

                    <div className="mt-5 flex items-center gap-2">
                      {flagSrc(t) && (
                        <img
                          src={flagSrc(t)}
                          alt={t.countryName}
                          className="h-5 rounded shadow"
                        />
                      )}
                      {!flagSrc(t) && <span>{t.flag}</span>}
                      <span className="text-blue-200 text-xs">{t.countryName}</span>
                      {t.date && <span className="ml-auto text-blue-300 text-xs">{t.date}</span>}
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          )}
        </div>
      </section>

      {/* ALL TESTIMONIALS — FILTERABLE */}
      <section className="py-24 bg-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-3">
            All Client Reviews
          </h2>
          <p className="text-center text-gray-500 mb-10">
            Browse by visa category to find stories relevant to your journey
          </p>

          {/* CATEGORY FILTER */}
          <div className="flex flex-wrap justify-center gap-3 mb-10">
            {CATEGORIES.map((cat) => {
              const Icon = CATEGORY_ICONS[cat];
              return (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium text-sm transition ${activeCategory === cat
                      ? "bg-blue-600 text-white shadow-md"
                      : "bg-white text-gray-600 border border-gray-200 hover:bg-blue-50 hover:text-blue-600"
                    }`}
                >
                  <Icon className="text-sm" />
                  {cat}
                </button>
              );
            })}
          </div>

          {loading ? (
            <div className="flex justify-center items-center h-40">
              <FaSpinner className="animate-spin text-blue-600 text-3xl" />
            </div>
          ) : error ? (
            <p className="text-center text-red-500 py-10">{error}</p>
          ) : (
            <>
              {/* COUNT */}
              <p className="text-gray-500 text-sm mb-8 text-center">
                Showing{" "}
                <span className="font-semibold text-gray-800">{filtered.length}</span>{" "}
                review{filtered.length !== 1 ? "s" : ""}
                {activeCategory !== "All" && (
                  <span className="text-blue-600 font-medium">
                    {" "}for {activeCategory}
                  </span>
                )}
              </p>

              {/* GRID */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filtered.map((t) => (
                  <div
                    key={t._id}
                    className="bg-white rounded-2xl p-8 shadow-md hover:shadow-2xl hover:-translate-y-2 transition duration-300 flex flex-col"
                  >
                    <FaQuoteLeft className="text-blue-100 text-4xl mb-4" />

                    <p className="text-gray-600 italic text-sm leading-relaxed flex-1 mb-5">
                      "{t.text}"
                    </p>

                    <div className="border-t border-gray-100 pt-5">
                      <div className="flex items-center gap-4">
                        <img
                          src={avatarSrc(t)}
                          alt={t.name}
                          className="w-12 h-12 rounded-full object-cover shadow"
                          onError={(e) => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(t.name)}&background=1d4ed8&color=fff`; }}
                        />

                        <div className="flex-1 min-w-0">
                          <h4 className="font-bold text-gray-800 truncate">{t.name}</h4>
                          <p className="text-xs text-blue-600 font-medium truncate">
                            {t.visaType}
                          </p>
                          <StarRating rating={t.rating} />
                        </div>

                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                          {flagSrc(t) ? (
                            <img
                              src={flagSrc(t)}
                              alt={t.countryName}
                              className="h-5 rounded shadow"
                            />
                          ) : (
                            <span className="text-lg">{t.flag}</span>
                          )}
                          {t.date && <span className="text-xs text-gray-400">{t.date}</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {filtered.length === 0 && (
                <p className="text-center text-gray-400 py-16 text-lg">
                  No testimonials found for this category yet.
                </p>
              )}
            </>
          )}
        </div>
      </section>

      {/* TRUST INDICATORS */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-3">
            Why Clients Trust Lumis
          </h2>
          <p className="text-center text-gray-500 mb-14">
            Our commitment to excellence sets us apart
          </p>

          <div className="grid md:grid-cols-3 gap-10">
            {[
              {
                icon: "🏆",
                title: "Proven Track Record",
                desc: "Over 1,200 successful visa approvals across 16+ countries with a 98% success rate.",
              },
              {
                icon: "🤝",
                title: "Personalized Guidance",
                desc: "Every client gets a dedicated consultant who understands their unique immigration goals.",
              },
              {
                icon: "🔒",
                title: "Transparent Process",
                desc: "No hidden fees, no surprises. We keep you fully informed at every stage of your application.",
              },
              {
                icon: "⚡",
                title: "Fast Processing",
                desc: "We submit applications efficiently to minimize delays and get you to your destination sooner.",
              },
              {
                icon: "📋",
                title: "Document Expertise",
                desc: "Expert guidance on every document required — we check, prepare, and verify everything.",
              },
              {
                icon: "🌍",
                title: "Global Reach",
                desc: "From Canada to Singapore, our network and expertise spans top immigration destinations worldwide.",
              },
            ].map(({ icon, title, desc }) => (
              <div
                key={title}
                className="text-center p-8 bg-white rounded-xl border border-gray-100 shadow hover:shadow-xl hover:-translate-y-2 transition duration-300"
              >
                <div className="text-5xl mb-4">{icon}</div>
                <h3 className="font-bold text-lg mb-3">{title}</h3>
                <p className="text-gray-500 text-sm">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SHARE YOUR STORY CTA */}
      <section className="py-20 bg-gray-100">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="text-5xl mb-5">💬</div>
          <h2 className="text-3xl font-bold mb-4">Got a Success Story?</h2>
          <p className="text-gray-500 mb-8">
            We'd love to hear about your experience with Lumis. Share your story
            and inspire others who are beginning their immigration journey.
          </p>
          <a
            href="https://wa.me/971547662665"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-green-500 hover:bg-green-600 text-white px-8 py-3 rounded-full font-semibold shadow-md transition"
          >
            <FaWhatsapp size={20} />
            Share on WhatsApp
          </a>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="bg-blue-600 text-white py-20 text-center">
        <h2 className="text-3xl font-bold mb-4">
          Start Your Own Success Story
        </h2>
        <p className="mb-8 text-blue-100 max-w-xl mx-auto">
          Join hundreds of happy clients who trusted Lumis Documents Clearing
          for their immigration journey. Get expert guidance today.
        </p>

        <div className="flex justify-center gap-4 flex-wrap">
          <Link
            to="/contact"
            className="bg-white text-blue-600 px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition shadow-md"
          >
            Get Free Consultation
          </Link>
          <Link
            to="/countries"
            className="border border-white text-white px-8 py-3 rounded-full font-semibold hover:bg-white hover:text-blue-600 transition"
          >
            Explore Countries
          </Link>
        </div>
      </section>

      {/* FLOATING CONTACT WIDGET */}
      <div className="fixed bottom-6 right-6 flex flex-col items-end gap-4 z-50">
        <div className="bg-white shadow-xl rounded-lg p-4 max-w-xs animate-bounce">
          <p className="text-sm text-gray-700">💬 Need help with your visa?</p>
          <p className="text-sm font-semibold text-gray-900">
            Chat with us on WhatsApp
          </p>
        </div>

        <Link to="/contact" className="group flex items-center">
          <span className="mr-3 bg-gray-900 text-white text-sm px-3 py-1 rounded opacity-0 group-hover:opacity-100 transition">
            Free Consultation
          </span>
          <div className="bg-blue-600 text-white p-4 rounded-full shadow-lg hover:scale-110 transition">
            <FaPhoneAlt size={20} />
          </div>
        </Link>

        <a
          href="https://wa.me/971547662665"
          target="_blank"
          rel="noopener noreferrer"
          className="group flex items-center"
        >
          <span className="mr-3 bg-gray-900 text-white text-sm px-3 py-1 rounded opacity-0 group-hover:opacity-100 transition">
            Chat on WhatsApp
          </span>
          <div className="bg-green-500 text-white p-4 rounded-full shadow-lg hover:scale-110 transition">
            <FaWhatsapp size={22} />
          </div>
        </a>
      </div>
    </div>
  );
}

export default Testimonials;
